import React, { useMemo, useCallback, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import BrickShapeRenderer from "./BrickShapeRenderer";
import { OptimizedBrick } from "../utils/brickOptimizer";
import "./OptimizedBrickLayout.css";

interface OptimizedBrickLayoutProps {
  bricks: OptimizedBrick[];
  width: number;
  height: number;
  pixelSize: number;
  is3D?: boolean;
}

interface Viewport {
  x: number;
  y: number;
  width: number;
  height: number;
}

const DEFAULT_ROTATION = { x: -28, y: 40 };
const DEFAULT_SCALE = 1.2;

const PRESETS = {
  default: DEFAULT_ROTATION,
  front: { x: -28, y: 0 }, // straight at the model's front face
  corner: { x: -28, y: 45 }, // the classic isometric corner
  top: { x: 24, y: 35 }, // looking down from above
} as const;

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

interface BrickGridProps {
  visibleBricks: OptimizedBrick[];
  canvasSize: { width: number; height: number };
  pixelSize: number;
  is3D: boolean;
  rotation: { x: number; y: number };
  scale: number;
  emptyMessage: string;
}

const BrickGrid = React.memo<BrickGridProps>(
  ({
    visibleBricks,
    canvasSize,
    pixelSize,
    is3D,
    rotation,
    scale,
    emptyMessage,
  }) => {
    if (visibleBricks.length === 0) {
      return (
        <div className="empty-layout">
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className="brick-grid-info">
        <div className="grid-overlay" />
        <div className="brick-content">
          <BrickShapeRenderer
            bricks={visibleBricks}
            canvasSize={canvasSize}
            pixelSize={pixelSize}
            is3D={is3D}
            rotation={rotation}
            scale={scale}
          />
        </div>
      </div>
    );
  }
);

const OptimizedBrickLayout: React.FC<OptimizedBrickLayoutProps> = ({
  bricks,
  width,
  height,
  pixelSize,
  is3D = false,
}) => {
  const { t } = useTranslation();
  const [viewport] = useState<Viewport>({
    x: 0,
    y: 0,
    width,
    height,
  });

  const [rotation, setRotation] = useState(DEFAULT_ROTATION);
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [isDragging, setIsDragging] = useState(false);
  const [show3dHelp, setShow3dHelp] = useState(false);

  const lastPointerRef = useRef<{ x: number; y: number } | null>(null);

  const canvasSize = useMemo(() => {
    if (is3D) {
      const maxWidth = Math.max(width, height) * pixelSize * 1.5;
      const maxHeight = Math.max(width, height) * pixelSize * 1.5;
      return {
        width: Math.min(maxWidth, 800),
        height: Math.min(maxHeight, 600),
      };
    }
    return {
      width: Math.min(width * pixelSize, 800),
      height: Math.min(height * pixelSize, 600),
    };
  }, [width, height, pixelSize, is3D]);

  const visibleBricks = useMemo(() => {
    if (!is3D || bricks.length < 1000) return bricks;

    return bricks.filter((brick) => {
      return (
        brick.x * pixelSize < viewport.x + canvasSize.width &&
        brick.y * pixelSize < viewport.y + canvasSize.height &&
        (brick.x + brick.brickType.width) * pixelSize > viewport.x &&
        (brick.y + brick.brickType.height) * pixelSize > viewport.y
      );
    });
  }, [bricks, pixelSize, viewport, canvasSize.width, canvasSize.height, is3D]);

  const isSubsetRendering =
    is3D && bricks.length >= 1000 && visibleBricks.length < bricks.length;

  const applyPreset = useCallback((key: keyof typeof PRESETS) => {
    setRotation({ ...PRESETS[key] });
  }, []);

  const resetView = useCallback(() => {
    setScale(DEFAULT_SCALE);
    setRotation({ ...DEFAULT_ROTATION });
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!is3D) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      setIsDragging(true);
      lastPointerRef.current = { x: e.clientX, y: e.clientY };
    },
    [is3D],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!is3D || !isDragging || !lastPointerRef.current) return;
      const last = lastPointerRef.current;
      const dx = e.clientX - last.x;
      const dy = e.clientY - last.y;
      lastPointerRef.current = { x: e.clientX, y: e.clientY };

      setRotation((prev) => ({
        x: clamp(prev.x - dy * 0.35, -72, 24),
        y: prev.y + dx * 0.45,
      }));
    },
    [is3D, isDragging],
  );

  const endDrag = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    setIsDragging(false);
    lastPointerRef.current = null;
  }, []);

  /** Safety: release capture if pointer is lost (e.g. trackpad gesture) */
  useEffect(() => {
    if (!is3D) return;
    const onWinPointerUp = () => {
      setIsDragging(false);
      lastPointerRef.current = null;
    };
    window.addEventListener("pointerup", onWinPointerUp);
    window.addEventListener("pointercancel", onWinPointerUp);
    return () => {
      window.removeEventListener("pointerup", onWinPointerUp);
      window.removeEventListener("pointercancel", onWinPointerUp);
    };
  }, [is3D]);

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      if (!is3D) return;
      e.preventDefault();
      setScale((prev) => clamp(prev + e.deltaY * -0.0012, 0.45, 3.2));
    },
    [is3D],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!is3D) return;
      const step = e.shiftKey ? 8 : 3;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        setRotation((r) => ({ ...r, y: r.y - step }));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setRotation((r) => ({ ...r, y: r.y + step }));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setRotation((r) => ({ ...r, x: clamp(r.x + step, -72, 24) }));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setRotation((r) => ({ ...r, x: clamp(r.x - step, -72, 24) }));
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        setScale((s) => clamp(s + 0.15, 0.45, 3.2));
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        setScale((s) => clamp(s - 0.15, 0.45, 3.2));
      } else if (e.key === "0") {
        e.preventDefault();
        resetView();
      }
    },
    [is3D, resetView],
  );

  return (
    <div className="optimized-brick-layout">
      <div className="layout-header">
        <h3>{t("instructions.legoLayout")}</h3>

        <div className="layout-stats">
          <div className="stat-item">
            <span>{t("instructions.totalBricks")}:</span>
            <strong>{bricks.length.toLocaleString()}</strong>
          </div>

          <div className="stat-item">
            <span>{t("instructions.gridSize")}:</span>
            <strong>
              {width} × {height}
            </strong>
          </div>

          {is3D && (
            <>
              <div className="stat-item stat-item--zoom">
                <span>{t("viewer3D.zoom")}:</span>
                <strong>{Math.round(scale * 100)}%</strong>
              </div>

              <div className="viewer3d-toolbar" role="toolbar" aria-label={t("viewer3D.toolbarLabel")}>
                <div className="viewer3d-presets">
                  <button
                    type="button"
                    className="viewer3d-chip"
                    onClick={() => applyPreset("default")}
                  >
                    {t("viewer3D.presetDefault")}
                  </button>
                  <button
                    type="button"
                    className="viewer3d-chip"
                    onClick={() => applyPreset("front")}
                  >
                    {t("viewer3D.presetFront")}
                  </button>
                  <button
                    type="button"
                    className="viewer3d-chip"
                    onClick={() => applyPreset("corner")}
                  >
                    {t("viewer3D.presetCorner")}
                  </button>
                  <button
                    type="button"
                    className="viewer3d-chip"
                    onClick={() => applyPreset("top")}
                  >
                    {t("viewer3D.presetTop")}
                  </button>
                </div>
                <div className="controls-3d">
                  <button
                    type="button"
                    onClick={() => setScale((prev) => clamp(prev + 0.2, 0.45, 3.2))}
                  >
                    {t("common.zoomIn")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setScale((prev) => clamp(prev - 0.2, 0.45, 3.2))}
                  >
                    {t("common.zoomOut")}
                  </button>
                  <button type="button" onClick={resetView}>
                    {t("common.reset")}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {is3D && isSubsetRendering && (
        <p className="viewer3d-performance-note" role="status">
          {t("viewer3D.renderingSubset", {
            shown: visibleBricks.length.toLocaleString(),
            total: bricks.length.toLocaleString(),
          })}
        </p>
      )}

      {is3D && (
        <div className="viewer3d-help-row">
          <button
            type="button"
            className="viewer3d-help-toggle"
            onClick={() => setShow3dHelp((v) => !v)}
            aria-expanded={show3dHelp}
          >
            {show3dHelp ? t("viewer3D.helpToggleHide") : t("viewer3D.helpToggleShow")}
          </button>
          {show3dHelp && (
            <ul className="viewer3d-help-list">
              <li>{t("viewer3D.helpDrag")}</li>
              <li>{t("viewer3D.helpScroll")}</li>
              <li>{t("viewer3D.helpKeys")}</li>
              <li>{t("viewer3D.helpButtons")}</li>
            </ul>
          )}
        </div>
      )}

      <div
        className={`brick-viewport ${is3D ? "viewport-3d" : "viewport-2d"}`}
        style={{
          width: `${canvasSize.width}px`,
          maxWidth: "100%",
          height: "auto",
          cursor: is3D ? (isDragging ? "grabbing" : "grab") : "default",
          touchAction: is3D ? "none" : undefined,
        }}
        tabIndex={is3D ? 0 : undefined}
        role={is3D ? "application" : "img"}
        aria-label={
          is3D ? t("viewer3D.ariaViewport") : t("converter.pixelatedVersion")
        }
        aria-describedby={is3D ? "viewer3d-hint" : undefined}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onPointerLeave={(e) => {
          if (!isDragging) return;
          if (!e.currentTarget.hasPointerCapture(e.pointerId)) endDrag(e);
        }}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
      >
        <BrickGrid
          visibleBricks={visibleBricks}
          canvasSize={canvasSize}
          pixelSize={pixelSize}
          is3D={is3D}
          rotation={rotation}
          scale={scale}
          emptyMessage={t("instructions.noBricksFound")}
        />
      </div>

      {is3D && (
        <p className="view-hint" id="viewer3d-hint">
          {t("viewer3D.viewHint")}
        </p>
      )}
    </div>
  );
};

export default React.memo(OptimizedBrickLayout);
